import {
  Badge,
  Box,
  Text,
  HStack,
  Heading,
  SimpleGrid,
  VStack,
  StackDivider,
} from "@chakra-ui/react";
import React from "react";
import { CustomerInfo } from "../../Models/CustomerInfo";
import { format } from "date-fns";
import { CustomerOverviewSnapshot } from "../../Models/CustomerOverviewSnapshot";
import { CustomerRunbookExecution } from "../../Models/CustomerRunbookExecution";
import { activeTenant } from "../../Features/Tenants/tenantSlice";
import { useAppSelector } from "../../hooks";

interface ICustomerInfoTableProps {
  items: CustomerInfo[];
  customerOverviewSnapshot?: CustomerOverviewSnapshot | null;
  customerRunbookExecutions: CustomerRunbookExecution[];
}

const CustomerInfoTable: React.FC<ICustomerInfoTableProps> = ({
  items,
  customerOverviewSnapshot,
  customerRunbookExecutions,
}) => {
  const normalizeStatus = (value?: string | null) => {
    if (!value) {
      return "Unknown";
    }

    return value;
  };

  const parseDetails = (detailsJson?: string) => {
    if (!detailsJson) {
      return null;
    }

    try {
      return JSON.parse(detailsJson);
    } catch {
      return null;
    }
  };

  const getRunbookStatus = (detailsJson?: string) => {
    if (!detailsJson) {
      return "Unknown";
    }

    try {
      const details = JSON.parse(detailsJson);
      return normalizeStatus(details?.latestRunbookJob?.Status);
    } catch {
      return "Unknown";
    }
  };

  const getLogicAppStatus = (detailsJson?: string) => {
    if (!detailsJson) {
      return "Unknown";
    }

    try {
      const details = JSON.parse(detailsJson);
      const logicApps = details?.logicApps;
      const count = Number(logicApps?.count ?? 0);
      const items = Array.isArray(logicApps?.items) ? logicApps.items : [];

      if (count === 0 || items.length === 0) {
        return "Not Configured";
      }

      const statuses = items
        .map((item: any) => (item?.LastRunStatus || "").toString().toLowerCase())
        .filter((status: string) => status.length > 0);

      if (statuses.some((status: string) => status.includes("fail"))) {
        return "Failed";
      }

      if (statuses.some((status: string) => status.includes("running"))) {
        return "Running";
      }

      if (statuses.some((status: string) => status.includes("succeed"))) {
        return "Succeeded";
      }

      return "Unknown";
    } catch {
      return "Unknown";
    }
  };

  const getStatusColorScheme = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    if (
      normalizedStatus.includes("success") ||
      normalizedStatus.includes("succeed")
    ) {
      return "green";
    }

    if (
      normalizedStatus.includes("completed") ||
      normalizedStatus.includes("enabled")
    ) {
      return "green";
    }

    if (normalizedStatus.includes("fail")) {
      return "red";
    }

    if (normalizedStatus.includes("running")) {
      return "blue";
    }

    if (normalizedStatus.includes("not configured")) {
      return "gray";
    }

    return "yellow";
  };

  const details = parseDetails(customerOverviewSnapshot?.detailsJson);

  const logicAppItems = Array.isArray(details?.logicApps?.items)
    ? details.logicApps.items
    : [];

  const runbookItems = Array.from(
    customerRunbookExecutions.reduce((latestByName, execution) => {
      const name = execution.runbookName || "Unknown Runbook";
      if (!latestByName.has(name)) {
        latestByName.set(name, execution);
      }
      return latestByName;
    }, new Map<string, CustomerRunbookExecution>()).values()
  );

  const powerBiReport = details?.powerBi?.report;
  const powerBiUsageReport = details?.powerBi?.usageReport;
  const cost = details?.cost;

  const activeTenantSlice = useAppSelector(activeTenant);
  const tenant = activeTenantSlice?.tenant;

  const powerBiRows = [
    {
      label: "Report",
      name:
        powerBiReport?.ReportName ||
        tenant?.powerBIReportId ||
        "Customer Report",
      status: normalizeStatus(
        powerBiReport?.Status || customerOverviewSnapshot?.powerBiReportStatus
      ),
      lastRefreshUtc: powerBiReport?.LastRefreshUtc,
    },
    {
      label: "Usage report",
      name:
        powerBiUsageReport?.ReportName ||
        tenant?.powerBIUsageReportId ||
        "Usage Report",
      status: normalizeStatus(
        powerBiUsageReport?.Status ||
          customerOverviewSnapshot?.powerBiUsageReportStatus
      ),
      lastRefreshUtc: powerBiUsageReport?.LastRefreshUtc,
    },
  ];

  const costAmount = Number(
    cost?.Amount ?? customerOverviewSnapshot?.costLast30Days
  );
  const costCurrency =
    cost?.Currency ?? customerOverviewSnapshot?.costCurrency;
  const costRowCount = Number(cost?.RowCount ?? 0);
  const costWindowStartUtc =
    cost?.WindowStartUtc ?? customerOverviewSnapshot?.costWindowStartUtc;
  const costWindowEndUtc =
    cost?.WindowEndUtc ?? customerOverviewSnapshot?.costWindowEndUtc;
  const hasCostData = Number.isFinite(costAmount) && Boolean(costCurrency);

  const InfoRow: React.FC<{
    title: string;
    content: string | number | null;
    maxH?: string;
    minH?: string;
  }> = ({ title, content, maxH = "none", minH = "1rem" }) => (
    <>
      <Box textAlign={"left"} minW="100%">
        <Heading
          size="sm"
          fontWeight={"var(--chakra-fontWeights-semibold)"}
          fontFamily="var(--chakra-fonts-body)"
          mb="0.5rem"
        >
          {title}
        </Heading>
        <Text
          fontSize="sm"
          maxH={maxH}
          minH={minH}
          wordBreak="break-word"
          whiteSpace="normal"
        >
          {content}
        </Text>
      </Box>
    </>
  );

  const powerBiStatus = normalizeStatus(
    customerOverviewSnapshot?.powerBiReportStatus
  );
  const runbookStatus = runbookItems.length > 0
    ? normalizeStatus(runbookItems[0].status)
    : getRunbookStatus(customerOverviewSnapshot?.detailsJson);
  const logicAppStatus = getLogicAppStatus(customerOverviewSnapshot?.detailsJson);
  const lastSnapshot = customerOverviewSnapshot?.lastCollectedAtUtc
    ? format(
        new Date(customerOverviewSnapshot.lastCollectedAtUtc),
        "dd.MM.yyyy HH:mm"
      )
    : "Unknown";

  return (
    <>
      <Box>
        {items.map((i) => (
          <SimpleGrid
            p="2"
            gap={6}
            key={i.azureTenantId}
            columns={{ base: 1, md: 2, xl: 3 }}
          >
            <VStack
              textAlign={"left"}
              align="stretch"
              divider={<StackDivider borderColor="gray.200" />}
              spacing={3}
            >
              <InfoRow title="Customer Name" content={i.customerName ?? ""} />
              <InfoRow title="Contact Name" content={i.contactName ?? ""} />
              <InfoRow title="Contact Mobile" content={i.contactMobile ?? ""} />
              <InfoRow title="Contact Email" content={i.contactEmail ?? ""} />
              <InfoRow
                title="Onboarded At"
                content={
                  i.onboardedAt
                    ? format(new Date(i.onboardedAt), "dd.MM.yyyy")
                    : null
                }
              />
              <InfoRow title="Subscriptions" content={i.subscriptions ?? null} />
              <InfoRow title="Customer Price" content={i.customerPrice ?? null} />
            </VStack>

            <VStack
              textAlign={"left"}
              align="stretch"
              divider={<StackDivider borderColor="gray.200" />}
              spacing={3}
            >
              <InfoRow title="Azure Tenant ID" content={tenant?.azureTenantId ?? ""} />
              <InfoRow
                title="Azure Analytics ID"
                content={tenant?.azureAnalyticsId ?? ""}
              />
              <InfoRow title="CC Report ID" content={tenant?.powerBIReportId ?? ""} />
              <InfoRow
                title="Usage Report ID"
                content={tenant?.powerBIUsageReportId ?? ""}
              />
              <InfoRow title="CRM ID" content={tenant?.crmCustomerId ?? ""} />
              <InfoRow
                title="Customer Short Name"
                content={tenant?.customerShortName ?? ""}
              />
            </VStack>

            <VStack
              textAlign={"left"}
              align="stretch"
              spacing={4}
              bg="gray.50"
              borderRadius="md"
              p={4}
            >
              <Heading
                size="sm"
                fontWeight={"var(--chakra-fontWeights-semibold)"}
                fontFamily="var(--chakra-fonts-body)"
              >
                Detailed Status Overview
              </Heading>

              <Box>
                <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase">
                  Power BI Reports
                </Text>
                <VStack align="stretch" spacing={2}>
                  {powerBiRows.map((report) => (
                    <Box key={`${report.name}-${report.status}`}>
                      <Text fontSize="xs" color="gray.500">
                        {report.label}
                      </Text>
                      <HStack
                        justifyContent="space-between"
                        align="start"
                        spacing={3}
                      >
                        <Text
                          fontSize="sm"
                          color="gray.700"
                          wordBreak="break-word"
                          flex="1"
                        >
                          {report.name}
                        </Text>
                        <Badge colorScheme={getStatusColorScheme(report.status)}>
                          {report.status}
                        </Badge>
                      </HStack>
                      {report.lastRefreshUtc ? (
                        <Text fontSize="xs" color="gray.500">
                          Last refresh: {format(new Date(report.lastRefreshUtc), "dd.MM.yyyy HH:mm")}
                        </Text>
                      ) : null}
                    </Box>
                  ))}
                </VStack>
              </Box>

              <Box>
                <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase">
                  Runbooks
                </Text>
                <VStack align="stretch" spacing={2}>
                  {runbookItems.length > 0 ? (
                    runbookItems.map((runbook) => (
                      <HStack
                        key={runbook.jobId || runbook.runbookName || "runbook"}
                        justifyContent="space-between"
                        align="start"
                        spacing={3}
                      >
                        <Text fontSize="sm" color="gray.700" wordBreak="break-word" flex="1">
                          {runbook.runbookName || "Unknown Runbook"}
                        </Text>
                        <Badge
                          colorScheme={getStatusColorScheme(
                            normalizeStatus(runbook.status)
                          )}
                        >
                          {normalizeStatus(runbook.status)}
                        </Badge>
                      </HStack>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No runbook data
                    </Text>
                  )}
                </VStack>
              </Box>

              <Box>
                <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase">
                  Cost
                </Text>
                {hasCostData ? (
                  <>
                    <HStack justifyContent="space-between" align="start" spacing={3}>
                      <Text fontSize="sm" color="gray.700" flex="1">
                        Last 30 days
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {costAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} {costCurrency}
                      </Text>
                    </HStack>
                    {costWindowStartUtc && costWindowEndUtc ? (
                      <Text fontSize="xs" color="gray.500">
                        {format(new Date(costWindowStartUtc), "dd.MM.yyyy")} - {format(new Date(costWindowEndUtc), "dd.MM.yyyy")}
                        {costRowCount > 0 ? ` (${costRowCount} rows)` : ""}
                      </Text>
                    ) : null}
                  </>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No cost data
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="xs" color="gray.500" mb={2} textTransform="uppercase">
                  Logic Apps
                </Text>
                <VStack align="stretch" spacing={2}>
                  {logicAppItems.length > 0 ? (
                    logicAppItems.map((logicApp: any) => {
                      const logicStatus = normalizeStatus(
                        logicApp?.LastRunStatus || logicApp?.State
                      );

                      return (
                        <HStack
                          key={logicApp?.Name || logicApp?.AccessEndpoint || "logicapp"}
                          justifyContent="space-between"
                          align="start"
                          spacing={3}
                        >
                          <Text
                            fontSize="sm"
                            color="gray.700"
                            wordBreak="break-word"
                            flex="1"
                          >
                            {logicApp?.Name || "Unknown Logic App"}
                          </Text>
                          <Badge colorScheme={getStatusColorScheme(logicStatus)}>
                            {logicStatus}
                          </Badge>
                        </HStack>
                      );
                    })
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      {logicAppStatus === "Not Configured"
                        ? "No logic apps configured"
                        : "No logic app data"}
                    </Text>
                  )}
                </VStack>
              </Box>

              <Box>
                <Text fontSize="xs" color="gray.500">
                  Last snapshot: {lastSnapshot}
                </Text>
                <HStack spacing={2} pt={2}>
                  <Text fontSize="xs" color="gray.600">
                    Summary
                  </Text>
                  <Badge colorScheme={getStatusColorScheme(powerBiStatus)}>
                    BI: {powerBiStatus}
                  </Badge>
                  <Badge colorScheme={getStatusColorScheme(runbookStatus)}>
                    RB: {runbookStatus}
                  </Badge>
                </HStack>
              </Box>
            </VStack>
          </SimpleGrid>
        ))}
      </Box>
    </>
  );
};

export default CustomerInfoTable;
